import type {
  DisputeExportRecord,
  DisputeListParams,
  ExportEnvelope,
  BinaryExportOptions,
  ExportOptions,
  JsonExportOptions,
  MerchantAuditLogExportRecord,
  MerchantAuditLogListParams,
  PayoutExportRecord,
  PayoutListParams,
  RefundExportRecord,
  RefundListParams,
  PaymentListFilterConstraints,
  ProfilePaymentListFilterConstraints,
  SubscriptionExportRecord,
  SubscriptionListParams,
  TransactionExportRecord,
} from '../types';
import type { RequestFn, RequestOptions } from '../client';
import { runExport } from '../exportDownload';

/** Query value shape `RequestOptions.query` accepts. */
type Query = NonNullable<RequestOptions['query']>;

/**
 * Emit a list as a file.
 *
 * Every method here mirrors one list endpoint and takes **that list's own
 * filters**, unchanged — there is no second filter vocabulary for exports. The
 * shop scope is resolved the same way too, so an export cannot show a shop its
 * list would not: a shop-scoped token exports its own shop, and naming another
 * is refused rather than ignored.
 *
 * Every export is **one read**, not a paged walk, and is bounded at 25,000
 * rows (500 for `pdf`, which is a document rather than a data feed). Over the
 * bound the request is refused with `DE_07 export_row_limit_exceeded` carrying
 * `{entity, max_rows}` — a truncated finance file that reports success is the
 * thing these endpoints exist not to produce. When too many exports are
 * already running the server answers `429` `IR_52` (`export_capacity_busy`);
 * a `GET` export is retried after its `Retry-After` like any other `429`.
 *
 * `csv` and `json` are **streamed**: the server counts the match set, sends
 * that count in `X-Delopay-Record-Count` before the first byte, and writes the
 * rows as it reads them. Each method holds the finished body to that count and
 * rejects with `EXPORT_INCOMPLETE` rather than resolve to a file missing rows —
 * see {@link ExportTransferOptions}, which also carries the download's
 * `onProgress`, `signal` and (idle) `timeout`. `pdf` stays a buffered body.
 *
 * `format` selects an `Accept` header, never a request field: an export reuses
 * its list's filter model as literally the same type, and several of those are
 * `deny_unknown_fields`, so a `format` key would be a 400. It defaults to
 * `json`, which resolves to a typed {@link ExportEnvelope}; `csv` and `pdf`
 * resolve to a `Blob`.
 */
export class Export {
  constructor(private readonly request: RequestFn) {}

  /**
   * Transactions. `POST /export/transactions`
   *
   * The payments `payments.listByFilter` would list for the same body, over the
   * whole match set: same filters, same order, same scope. `limit` and
   * `offset` are ignored — the page is what an export does away with. No time
   * window is required; the row bound refuses an export that matches too much.
   */
  async transactions(
    params?: PaymentListFilterConstraints,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord>>;
  async transactions(
    params: PaymentListFilterConstraints | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async transactions(
    params: PaymentListFilterConstraints | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord> | Blob>;
  async transactions(
    params?: PaymentListFilterConstraints,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord> | Blob> {
    return runExport(this.request, 'POST', '/export/transactions', { body: params ?? {} }, options);
  }

  /**
   * The caller's own shop's transactions.
   * `POST /export/profile/transactions`
   *
   * The shop-scoped twin of {@link Export.transactions}, taking the body
   * `payments.listByProfileFilter` sends and scoped exactly as that list is: to
   * the shop on the token. Gated on the profile-level payment permission, which
   * a shop-scoped role can hold where the merchant-level one cannot. Its rows
   * carry no `connector_settlement_status`, which a shop viewer is not shown.
   */
  async transactionsForProfile(
    params?: ProfilePaymentListFilterConstraints,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord>>;
  async transactionsForProfile(
    params: ProfilePaymentListFilterConstraints | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async transactionsForProfile(
    params: ProfilePaymentListFilterConstraints | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord> | Blob>;
  async transactionsForProfile(
    params?: ProfilePaymentListFilterConstraints,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<TransactionExportRecord> | Blob> {
    return runExport(
      this.request,
      'POST',
      '/export/profile/transactions',
      { body: params ?? {} },
      options,
    );
  }

  /** Refunds. `POST /refunds/list/export` */
  async refunds(
    params?: RefundListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord>>;
  async refunds(params: RefundListParams | undefined, options: BinaryExportOptions): Promise<Blob>;
  async refunds(
    params: RefundListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord> | Blob>;
  async refunds(
    params?: RefundListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord> | Blob> {
    return runExport(this.request, 'POST', '/refunds/list/export', { body: params ?? {} }, options);
  }

  /** The caller's own shop's refunds. `POST /refunds/profile/list/export` */
  async refundsForProfile(
    params?: RefundListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord>>;
  async refundsForProfile(
    params: RefundListParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async refundsForProfile(
    params: RefundListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord> | Blob>;
  async refundsForProfile(
    params?: RefundListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<RefundExportRecord> | Blob> {
    return runExport(
      this.request,
      'POST',
      '/refunds/profile/list/export',
      { body: params ?? {} },
      options,
    );
  }

  /** Disputes. `GET /disputes/list/export` */
  async disputes(
    params?: DisputeListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord>>;
  async disputes(
    params: DisputeListParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async disputes(
    params: DisputeListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord> | Blob>;
  async disputes(
    params?: DisputeListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord> | Blob> {
    return runExport(this.request, 'GET', '/disputes/list/export', queried(params), options);
  }

  /** The caller's own shop's disputes. `GET /disputes/profile/list/export` */
  async disputesForProfile(
    params?: DisputeListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord>>;
  async disputesForProfile(
    params: DisputeListParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async disputesForProfile(
    params: DisputeListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord> | Blob>;
  async disputesForProfile(
    params?: DisputeListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<DisputeExportRecord> | Blob> {
    return runExport(
      this.request,
      'GET',
      '/disputes/profile/list/export',
      queried(params),
      options,
    );
  }

  /** Payouts. `POST /payouts/list/export` */
  async payouts(
    params: PayoutListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord>>;
  async payouts(params: PayoutListParams, options: BinaryExportOptions): Promise<Blob>;
  async payouts(
    params: PayoutListParams,
    options: ExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord> | Blob>;
  async payouts(
    params: PayoutListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord> | Blob> {
    return runExport(this.request, 'POST', '/payouts/list/export', { body: params }, options);
  }

  /** The caller's own shop's payouts. `POST /payouts/profile/list/export` */
  async payoutsForProfile(
    params: PayoutListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord>>;
  async payoutsForProfile(params: PayoutListParams, options: BinaryExportOptions): Promise<Blob>;
  async payoutsForProfile(
    params: PayoutListParams,
    options: ExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord> | Blob>;
  async payoutsForProfile(
    params: PayoutListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<PayoutExportRecord> | Blob> {
    return runExport(
      this.request,
      'POST',
      '/payouts/profile/list/export',
      { body: params },
      options,
    );
  }

  /**
   * Subscriptions. `GET /subscriptions/list/export`
   *
   * `profileId` becomes the `X-Profile-Id` header, exactly as on the list. A
   * shop-scoped token exports its own shop whatever this says.
   */
  async subscriptions(
    profileId: string,
    params?: SubscriptionListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<SubscriptionExportRecord>>;
  async subscriptions(
    profileId: string,
    params: SubscriptionListParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async subscriptions(
    profileId: string,
    params: SubscriptionListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<SubscriptionExportRecord> | Blob>;
  async subscriptions(
    profileId: string,
    params?: SubscriptionListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<SubscriptionExportRecord> | Blob> {
    return runExport(
      this.request,
      'GET',
      '/subscriptions/list/export',
      queried(params, { 'X-Profile-Id': profileId }),
      options,
    );
  }

  /** Settlement statements. `GET /settlement/statements/export` */

  /** One period's settlement lines. `GET /settlement/lines/export` */

  /**
   * The merchant's own audit log. `GET /audit/export`
   *
   * Requires a dashboard JWT whose role holds the merchant-level `AuditLog`
   * permission — a shop-scoped role cannot hold it. There is no `merchant_id`
   * parameter: the scope comes from the token.
   */
  async auditLog(
    params?: MerchantAuditLogListParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<MerchantAuditLogExportRecord>>;
  async auditLog(
    params: MerchantAuditLogListParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async auditLog(
    params: MerchantAuditLogListParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<MerchantAuditLogExportRecord> | Blob>;
  async auditLog(
    params?: MerchantAuditLogListParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<MerchantAuditLogExportRecord> | Blob> {
    return runExport(this.request, 'GET', '/audit/export', queried(params), options);
  }
}

/** Request options for an export whose filters travel in the query. */
function queried(params: unknown, headers?: Record<string, string>): RequestOptions {
  return { query: (params ?? {}) as Query, ...(headers ? { headers } : {}) };
}
